import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  Paper,
  Tabs,
  Tab,
  Rating,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Switch,
  FormControlLabel,
  Slider,
  Stack,
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

function TabPanel({
  children, value, index, ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`feedback-tabpanel-${index}`}
      aria-labelledby={`feedback-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function ClientFeedbackSystem({ property, onUpdate }) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [feedbackSettings, setFeedbackSettings] = useState({
    autoSend: true,
    sendAfterHours: 2,
    reminderAfterDays: 3,
    maxReminders: 2,
    includeRating: true,
    includeComments: true,
    includeFollowUp: true,
  });

  // Mock feedback data
  const mockFeedback = [
    {
      id: 1,
      showingId: 1,
      clientName: 'John Smith',
      clientEmail: 'john.smith@email.com',
      propertyId: property?.id,
      rating: 4,
      comments: 'Great property, loved the location and layout. The kitchen is exactly what we were looking for. However, I\'m concerned about the parking situation and the HOA fees seem a bit high.',
      interested: true,
      priceRange: '$1.1M - $1.3M',
      timeline: '3-6 months',
      followUpRequired: true,
      followUpStatus: 'Pending',
      followUpNotes: 'Schedule follow-up call to discuss parking alternatives and HOA benefits',
      sentiment: 'positive',
      keywords: ['great', 'loved', 'concerned', 'high'],
      submittedAt: new Date('2024-07-18T16:30:00'),
      reminders: [
        { type: 'email', sent: true, date: new Date('2024-07-18T18:00:00') },
        { type: 'sms', sent: false, scheduled: new Date('2024-07-21T10:00:00') },
      ],
      agent: 'Sarah Johnson',
      showingDate: new Date('2024-07-18T14:00:00'),
    },
    {
      id: 2,
      showingId: 2,
      clientName: 'Sarah Johnson',
      clientEmail: 'sarah.j@email.com',
      propertyId: property?.id,
      rating: 5,
      comments: 'Absolutely love this property! The virtual tour was amazing and the agent was very helpful. The price is perfect for our budget and the neighborhood is exactly what we wanted.',
      interested: true,
      priceRange: '$1.2M - $1.4M',
      timeline: '1-2 months',
      followUpRequired: true,
      followUpStatus: 'Scheduled',
      followUpNotes: 'Client is very interested, schedule in-person showing',
      sentiment: 'very_positive',
      keywords: ['love', 'amazing', 'perfect', 'helpful'],
      submittedAt: new Date('2024-07-19T11:15:00'),
      reminders: [
        { type: 'email', sent: true, date: new Date('2024-07-19T13:15:00') },
      ],
      agent: 'Mike Davis',
      showingDate: new Date('2024-07-19T10:00:00'),
    },
    {
      id: 3,
      showingId: 3,
      clientName: 'Mike Davis',
      clientEmail: 'mike.davis@email.com',
      propertyId: property?.id,
      rating: 2,
      comments: 'The property is okay but the price is too high for what you get. The layout is a bit awkward and the backyard is smaller than expected. Not really what we\'re looking for.',
      interested: false,
      priceRange: '$900K - $1.1M',
      timeline: '1-2 months',
      followUpRequired: false,
      followUpStatus: 'Not Required',
      followUpNotes: 'Client not interested, focus on other properties',
      sentiment: 'negative',
      keywords: ['okay', 'high', 'awkward', 'smaller'],
      submittedAt: new Date('2024-07-17T17:45:00'),
      reminders: [
        { type: 'email', sent: true, date: new Date('2024-07-17T19:45:00') },
      ],
      agent: 'Lisa Wilson',
      showingDate: new Date('2024-07-17T16:00:00'),
    },
  ];

  const [feedback, setFeedback] = useState([]);
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [filterFollowUp, setFilterFollowUp] = useState('all');

  useEffect(() => {
    if (property) {
      setFeedback(mockFeedback.filter((f) => f.propertyId === property.id));
    }
  }, [property]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddFeedback = () => {
    setSelectedFeedback(null);
    setOpenDialog(true);
  };

  const handleEditFeedback = (feedbackItem) => {
    setSelectedFeedback(feedbackItem);
    setOpenDialog(true);
  };

  const handleDeleteFeedback = (feedbackId) => {
    setFeedback((prev) => prev.filter((f) => f.id !== feedbackId));
  };

  const handleSaveFeedback = (feedbackData) => {
    if (selectedFeedback) {
      setFeedback((prev) => prev.map((f) => (f.id === selectedFeedback.id ? { ...f, ...feedbackData } : f)));
    } else {
      const newFeedback = {
        id: Date.now(),
        ...feedbackData,
        submittedAt: new Date(),
        sentiment: analyzeSentiment(feedbackData.comments),
        keywords: extractKeywords(feedbackData.comments),
      };
      setFeedback((prev) => [...prev, newFeedback]);
    }
    setOpenDialog(false);
  };

  const analyzeSentiment = (text) => {
    const positiveWords = ['love', 'great', 'amazing', 'perfect', 'excellent', 'wonderful', 'beautiful'];
    const negativeWords = ['hate', 'terrible', 'awful', 'bad', 'disappointing', 'expensive', 'small'];

    const words = text.toLowerCase().split(' ');
    const positiveCount = words.filter((word) => positiveWords.includes(word)).length;
    const negativeCount = words.filter((word) => negativeWords.includes(word)).length;

    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  };

  const extractKeywords = (text) => {
    const commonKeywords = ['price', 'location', 'kitchen', 'bathroom', 'bedroom', 'parking', 'neighborhood', 'layout'];
    const words = text.toLowerCase().split(' ');
    return words.filter((word) => commonKeywords.includes(word)).slice(0, 5);
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'very_positive': return 'success';
      case 'positive': return 'success';
      case 'neutral': return 'default';
      case 'negative': return 'error';
      default: return 'default';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'very_positive': return <TrendingUpIcon color="success" />;
      case 'positive': return <TrendingUpIcon color="success" />;
      case 'neutral': return <TrendingUpIcon color="default" />;
      case 'negative': return <TrendingDownIcon color="error" />;
      default: return <TrendingUpIcon color="default" />;
    }
  };

  const getFeedbackStats = () => {
    const total = feedback.length;
    const avgRating = feedback.reduce((sum, f) => sum + f.rating, 0) / total;
    const interestedCount = feedback.filter((f) => f.interested).length;
    const followUpCount = feedback.filter((f) => f.followUpRequired).length;
    const positiveCount = feedback.filter((f) => f.sentiment === 'positive' || f.sentiment === 'very_positive').length;

    return {
      total,
      avgRating: avgRating.toFixed(1),
      interestedCount,
      followUpCount,
      positiveCount,
      positivePercentage: ((positiveCount / total) * 100).toFixed(1),
    };
  };

  const getFilteredFeedback = () => {
    let filtered = feedback;

    if (filterSentiment !== 'all') {
      filtered = filtered.filter((f) => f.sentiment === filterSentiment);
    }

    if (filterFollowUp !== 'all') {
      filtered = filtered.filter((f) => f.followUpStatus === filterFollowUp);
    }

    return filtered;
  };

  const formatDate = (date) => `${new Date(date).toLocaleDateString()} ${new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  const renderFeedbackOverview = () => {
    const stats = getFeedbackStats();

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Feedback Overview
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Feedback
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {stats.avgRating}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Rating
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight={700} color="info.main">
                  {stats.interestedCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Interested Clients
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {stats.followUpCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Follow-ups Required
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sentiment Analysis
                </Typography>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 2, mb: 2,
                }}
                >
                  <Typography variant="h4" color="success.main">
                    {stats.positivePercentage}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Positive Feedback
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={parseFloat(stats.positivePercentage)}
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Rating Distribution
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = feedback.filter((f) => f.rating === rating).length;
                    const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
                    return (
                      <Box key={rating} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 60 }}>
                          <Rating value={rating} readOnly size="small" />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 40 }}>
                          {count}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderFeedbackList = () => (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">Feedback List</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddFeedback}
        >
          Add Feedback
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sentiment</InputLabel>
                <Select
                  value={filterSentiment}
                  onChange={(e) => setFilterSentiment(e.target.value)}
                  label="Sentiment"
                >
                  <MenuItem value="all">All Sentiments</MenuItem>
                  <MenuItem value="very_positive">Very Positive</MenuItem>
                  <MenuItem value="positive">Positive</MenuItem>
                  <MenuItem value="neutral">Neutral</MenuItem>
                  <MenuItem value="negative">Negative</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Follow-up Status</InputLabel>
                <Select
                  value={filterFollowUp}
                  onChange={(e) => setFilterFollowUp(e.target.value)}
                  label="Follow-up Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Not Required">Not Required</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="outlined" startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Feedback List */}
      <Card>
        <CardContent>
          {getFilteredFeedback().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <FeedbackIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6">No feedback found</Typography>
              <Typography variant="body2">Try adjusting your filters or add new feedback</Typography>
            </Box>
          ) : (
            <List>
              {getFilteredFeedback().map((feedbackItem, index) => (
                <React.Fragment key={feedbackItem.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Badge
                        badgeContent={feedbackItem.followUpRequired ? '!' : '✓'}
                        color={feedbackItem.followUpRequired ? 'warning' : 'success'}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getSentimentIcon(feedbackItem.sentiment)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {feedbackItem.clientName}
                          </Typography>
                          <Rating value={feedbackItem.rating} readOnly size="small" />
                          <Chip
                            label={feedbackItem.sentiment}
                            color={getSentimentColor(feedbackItem.sentiment)}
                            size="small"
                          />
                          <Chip
                            label={feedbackItem.interested ? 'Interested' : 'Not Interested'}
                            color={feedbackItem.interested ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      )}
                      secondary={(
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(feedbackItem.submittedAt)}
                            {' '}
                            • Agent:
                            {feedbackItem.agent}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Price Range:
                            {' '}
                            {feedbackItem.priceRange}
                            {' '}
                            • Timeline:
                            {' '}
                            {feedbackItem.timeline}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {feedbackItem.comments}
                          </Typography>
                          {feedbackItem.keywords.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {feedbackItem.keywords.map((keyword, idx) => (
                                <Chip key={idx} label={keyword} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                              ))}
                            </Box>
                          )}
                          {feedbackItem.followUpRequired && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                Follow-up:
                                {' '}
                                {feedbackItem.followUpNotes}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Feedback">
                        <IconButton onClick={() => handleEditFeedback(feedbackItem)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Feedback">
                        <IconButton onClick={() => handleDeleteFeedback(feedbackItem.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < getFilteredFeedback().length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderAutomationSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Feedback Automation Settings
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Automated Feedback Collection
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={feedbackSettings.autoSend}
                    onChange={(e) => setFeedbackSettings((prev) => ({
                      ...prev,
                      autoSend: e.target.checked,
                    }))}
                  />
                )}
                label="Automatically send feedback requests"
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Send feedback request after showing (hours)
                </Typography>
                <Slider
                  value={feedbackSettings.sendAfterHours}
                  onChange={(e, value) => setFeedbackSettings((prev) => ({
                    ...prev,
                    sendAfterHours: value,
                  }))}
                  min={1}
                  max={24}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Send reminder after (days)
                </Typography>
                <Slider
                  value={feedbackSettings.reminderAfterDays}
                  onChange={(e, value) => setFeedbackSettings((prev) => ({
                    ...prev,
                    reminderAfterDays: value,
                  }))}
                  min={1}
                  max={7}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Feedback Form Options
              </Typography>

              <FormControlLabel
                control={(
                  <Switch
                    checked={feedbackSettings.includeRating}
                    onChange={(e) => setFeedbackSettings((prev) => ({
                      ...prev,
                      includeRating: e.target.checked,
                    }))}
                  />
                )}
                label="Include rating (1-5 stars)"
              />

              <FormControlLabel
                control={(
                  <Switch
                    checked={feedbackSettings.includeComments}
                    onChange={(e) => setFeedbackSettings((prev) => ({
                      ...prev,
                      includeComments: e.target.checked,
                    }))}
                  />
                )}
                label="Include comments section"
              />

              <FormControlLabel
                control={(
                  <Switch
                    checked={feedbackSettings.includeFollowUp}
                    onChange={(e) => setFeedbackSettings((prev) => ({
                      ...prev,
                      includeFollowUp: e.target.checked,
                    }))}
                  />
                )}
                label="Include follow-up questions"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Client Feedback System
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Overview" icon={<AnalyticsIcon />} />
        <Tab label="Feedback List" icon={<FeedbackIcon />} />
        <Tab label="Automation" icon={<SettingsIcon />} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        {renderFeedbackOverview()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderFeedbackList()}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {renderAutomationSettings()}
      </TabPanel>

      {/* Add/Edit Feedback Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedFeedback ? 'Edit Feedback' : 'Add New Feedback'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Client Name *"
                value={selectedFeedback?.clientName || ''}
                onChange={(e) => setSelectedFeedback((prev) => ({
                  ...prev,
                  clientName: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Client Email *"
                value={selectedFeedback?.clientEmail || ''}
                onChange={(e) => setSelectedFeedback((prev) => ({
                  ...prev,
                  clientEmail: e.target.value,
                }))}
                fullWidth
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Agent"
                value={selectedFeedback?.agent || ''}
                onChange={(e) => setSelectedFeedback((prev) => ({
                  ...prev,
                  agent: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rating</InputLabel>
                <Select
                  value={selectedFeedback?.rating || 5}
                  onChange={(e) => setSelectedFeedback((prev) => ({
                    ...prev,
                    rating: e.target.value,
                  }))}
                  label="Rating"
                >
                  <MenuItem value={5}>5 Stars</MenuItem>
                  <MenuItem value={4}>4 Stars</MenuItem>
                  <MenuItem value={3}>3 Stars</MenuItem>
                  <MenuItem value={2}>2 Stars</MenuItem>
                  <MenuItem value={1}>1 Star</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Comments *"
                value={selectedFeedback?.comments || ''}
                onChange={(e) => setSelectedFeedback((prev) => ({
                  ...prev,
                  comments: e.target.value,
                }))}
                fullWidth
                multiline
                rows={4}
                placeholder="Enter client feedback..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price Range"
                value={selectedFeedback?.priceRange || ''}
                onChange={(e) => setSelectedFeedback((prev) => ({
                  ...prev,
                  priceRange: e.target.value,
                }))}
                fullWidth
                placeholder="e.g., $1.1M - $1.3M"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Timeline"
                value={selectedFeedback?.timeline || ''}
                onChange={(e) => setSelectedFeedback((prev) => ({
                  ...prev,
                  timeline: e.target.value,
                }))}
                fullWidth
                placeholder="e.g., 3-6 months"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={selectedFeedback?.interested || false}
                    onChange={(e) => setSelectedFeedback((prev) => ({
                      ...prev,
                      interested: e.target.checked,
                    }))}
                  />
                )}
                label="Client is interested"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Follow-up Notes"
                value={selectedFeedback?.followUpNotes || ''}
                onChange={(e) => setSelectedFeedback((prev) => ({
                  ...prev,
                  followUpNotes: e.target.value,
                }))}
                fullWidth
                multiline
                rows={3}
                placeholder="Enter follow-up notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleSaveFeedback(selectedFeedback)}
            variant="contained"
            disabled={!selectedFeedback?.clientName || !selectedFeedback?.comments}
          >
            {selectedFeedback?.id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientFeedbackSystem;
